'use client';

/**
 * DomainSearch.tsx — domenesøk i heroen på /domener
 *
 * Validerer navnet i nettleseren (se lib/domain.ts) og viser resultater per
 * støttet domeneendelse med pris og «Be om registrering» (mailto). Sjekker IKKE
 * tilgjengelighet — det finnes ingen backend for det ennå, så siden sier det
 * ærlig i stedet for å vise «ledig»/«opptatt». Når et endepunkt finnes, kan
 * submit-handleren kalle det og resultatradene få en status.
 *
 * Tilgjengelighet: <form role="search"> med synlig etikett, hint koblet med
 * aria-describedby, aria-invalid + feilmelding, og et aria-live-område som
 * leser opp feil og resultater. Enter sender skjemaet.
 */

import { useId, useState, type FormEvent } from 'react';
import { ArrowRight, Search } from '@carbon/icons-react';
import { formatPrice, parseDomainQuery } from '@/lib/domain';
import type { DomainSearchContent, DomainTld } from '@/types/domain';
import styles from './domain.module.css';

interface DomainSearchProps {
  search: DomainSearchContent;
  /** Alle støttede endelser (brukes til validering) */
  tlds: DomainTld[];
  /** Valutaenhet etter prisen, f.eks. «kr» */
  currency: string;
}

/** Maks antall rader i resultatlisten (valgt endelse + populære) */
const MAX_RESULTS = 6;

type Result =
  | { kind: 'idle' }
  | { kind: 'error'; message: string }
  | { kind: 'results'; name: string; first: string | null };

export default function DomainSearch({ search, tlds, currency }: DomainSearchProps) {
  const uid = useId();
  const [value, setValue] = useState('');
  const [result, setResult] = useState<Result>({ kind: 'idle' });

  const supported = tlds.map((t) => t.tld);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = parseDomainQuery(value, supported);
    if (!parsed.ok) {
      setResult({ kind: 'error', message: search.errors[parsed.error] });
      return;
    }
    setResult({ kind: 'results', name: parsed.name, first: parsed.tld });
  };

  const invalid = result.kind === 'error';
  // Valgt endelse først, deretter populære endelser (maks MAX_RESULTS rader)
  const ordered =
    result.kind === 'results'
      ? [
          ...tlds.filter((t) => t.tld === result.first),
          ...tlds.filter((t) => t.popular && t.tld !== result.first),
        ].slice(0, MAX_RESULTS)
      : [];

  return (
    <div className={styles.search}>
      <form role="search" aria-label={search.label} onSubmit={onSubmit} noValidate>
        <label htmlFor={`${uid}-q`} className={styles.label}>
          {search.label}
        </label>
        <div className={styles.field}>
          <input
            id={`${uid}-q`}
            type="text"
            inputMode="url"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            className={styles.input}
            placeholder={search.placeholder}
            value={value}
            aria-invalid={invalid}
            aria-describedby={`${uid}-hint ${uid}-status`}
            onChange={(e) => setValue(e.target.value)}
          />
          <button type="submit" className={styles.submit}>
            <span>{search.button}</span>
            <Search size={20} aria-hidden />
          </button>
        </div>
        <p id={`${uid}-hint`} className={styles.hint}>
          {search.hint}
        </p>
      </form>

      <ul className={styles.chips} aria-label={search.chipsLabel}>
        {tlds
          .filter((t) => t.popular)
          .map((t) => (
            <li key={t.tld} className={styles.chip}>
              <span className={styles.chipTld}>.{t.tld}</span>
              <span className={styles.chipPrice}>{formatPrice(t.register, currency)}</span>
            </li>
          ))}
      </ul>

      {/* Leses opp av skjermlesere når feil eller resultater dukker opp */}
      <div id={`${uid}-status`} aria-live="polite" className={styles.status}>
        {result.kind === 'error' && (
          <p className={styles.error}>{result.message}</p>
        )}

        {result.kind === 'results' && (
          <div className={styles.results}>
            <h2 className={styles.resultsHeading}>{search.resultsHeading.replace('{name}', result.name)}</h2>
            <p className={styles.notice}>{search.notice}</p>
            <ul className={styles.resultList}>
              {ordered.map((t) => {
                const domain = `${result.name}.${t.tld}`;
                return (
                  <li key={t.tld} className={styles.resultRow}>
                    <span className={styles.resultName}>{domain}</span>
                    <span className={styles.resultPrice}>
                      {search.priceFrom} {formatPrice(t.register, currency)}
                    </span>
                    <a
                      className={styles.resultAction}
                      href={search.contactHref.replace('{domain}', encodeURIComponent(domain))}
                    >
                      {search.contactLabel}
                      <ArrowRight size={16} aria-hidden />
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
